import { defineStore } from 'pinia';
import { ref } from 'vue';
import useUserComposable from '@/composables/UserComposable';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';

import { useLinkStore } from '../../link/store';
import { useStepOutcomeStore } from '../modules/outcome/store';

const STEP_URL = 'v2/template/steps';

export const useStepStore = defineStore('step', () => {
    const { company_id } = useUserComposable();
    const linkStore = useLinkStore();
    const stepOutcomeStore = useStepOutcomeStore();

    const loadingStep = ref(false);
    const stepCount = ref(1);
    const activeStep = ref(null);
    const steps = ref([]);

    // step helpers
    function initateStepDetails() {
        return {
            label: null,
            description: null,
            assessment_id: null,
            block_id: null,
            company_id: null,
            entry: 0,
            code: null,
            block: null,
            before_text: null,
            after_text: null,
        };
    }

    function increaseStepCount() {
        stepCount.value++;
    }

    // this function takes up the array of steps from an existing assessment details and format them to reusable form in our assessment builder UI
    function populateAssessmentSteps(data) {
        // make sure to nullify the existing steps array
        steps.value = [];
        // we will nullify the existing links array in the beginning from here
        // we are doing it here because below, stepLinks are being populated in loop,
        // and we dont want to lose the links data everytime we request for new populateStepLinks
        linkStore.links = [];

        // now loop the steps and populate them
        data?.forEach((step) => {
            const stepObj = {
                id: step.id, // loadingStep step id is important for the frontend, while dealing with links we need step id
                code: step.code,
                label: step.label,
                description: step.description,
                assessment_id: step.assessment_id,
                block_id: step.block_id,
                company_id: step.company_id,
                entry: step.entry,
                block: step.block,
                before_text: step.before_text,
                after_text: step.after_text
            };
            steps.value.push(stepObj);
            // check if there are any link_to in the current step
            // if yes then populate the link states for that step too
            // normally link_to will be in array so send the whole array
            if (step.link_to.length) {
                linkStore.populateStepLinks(step.link_to);
            }
        });
    }

    // getters
    // this function will return the step id related to a block id
    function getStepDetailsByBlockId(blockId) {
        return steps.value?.find((step) => step.block_id === blockId);
    }

    function getStepIdByBlockId(blockId) {
        return steps.value?.find((step) => step.block_id === blockId)?.id;
    }

    const getStepLabelById = (stepId) => {
        return steps.value?.find((step) => step.id === stepId)?.label;
    };

    // actions
    const getStepDetailsById = async (stepId) => {
        try {
            const response = await GlobalRequest.getRequest(`${STEP_URL}/${stepId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                return response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch step details');
        }
    };

    const addStep = async (params) => {
        try {
            const response = await GlobalRequest.postRequest(STEP_URL, {
                ...params,
                company_id: company_id.value,
                description: params.description?.length > 255 ? params.description?.substring(0, 255) : params.description,
            });
            if (response.statusCode === 201) {
                // increase step counter
                increaseStepCount();
                // add the created step into steps array
                steps.value.push(response.data);
                showToast(response, 'Step created successfully');
            }
            return response.data;
        } catch (error) {
            showToast(error.response, error?.data?.message ?? 'Failed to create step');
        }
    };

    const updateStep = async (params, id) => {
        try {
            const stepBody = {
                label: params.label,
                description: params.description?.length > 255 ? params.description?.substring(0, 255) : params.description,
                assessment_id: params.assessment_id,
                block_id: params.block_id,
                company_id: company_id.value,
                entry: params.entry,
                code: params.code,
                before_text: params.before_text,
                after_text: params.after_text
            };
            const response = await GlobalRequest.patchRequest(`${STEP_URL}/${id}`, stepBody);
            if (response.statusCode === 200) {
                // update the particular step from the steps array here
                //  for now nothing is done
                showToast(response, 'Step updated successfully');
            }
            return response.data;
        } catch (error) {
            showToast(error.response, error?.data?.message ?? 'Failed to create step');
        }
    };

    const deleteStep = async (id) => {
        try {
            const response = await GlobalRequest.deleteRequest(`${STEP_URL}/${id}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                showToast(response, 'Step deleted successfully');
                // after deletion filter out from the existing steps
                steps.value = steps.value.filter((step) => step.id !== id);
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete step');
        }
    };

    // sets a step as active, if there are criterias i.e requirement for pass/fail criteria then it will be loaded/populated to stepOutcome states
    const setActiveStep = async (blockId) => {
        // here loadingStep value is being used to avoid users seeing active step details changes in the UI
        loadingStep.value = true;
        const stepId = getStepIdByBlockId(blockId);
        // if there is no any activeStep yet OR
        // if there is an existing active step and its id doesnt match the step id only then get step details
        if (!activeStep.value || activeStep.value?.id !== stepId) {
            activeStep.value = await getStepDetailsById(stepId);
            // if there are pass/fail criterias then load them in outcome states
            const criterias = activeStep.value?.requirement;
            // if (criterias?.length) {
            stepOutcomeStore.populateStepOutcomes(criterias);
            // }
        }
        loadingStep.value = false;
    };

    return {
        // states
        loadingStep,
        activeStep,
        steps,
        // getters
        getStepLabelById,
        // helpers
        initateStepDetails,
        populateAssessmentSteps,
        getStepDetailsByBlockId,
        // actions
        getStepDetailsById,
        addStep,
        setActiveStep,
        updateStep,
        deleteStep,
    };
});
